"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";

export interface AccordionItemProps {
  id?: string;
  defaultOpen?: boolean;
  className?: string;
  headerClassName?: string;
  header: React.ReactNode | string;
  contentClassName?: string;
  content: React.ReactNode | string;
}

const convertHeaderToLowercaseKebab = (header: string) => {
  if (typeof header !== "string") return "react.child";
  return header
    .toLocaleLowerCase()
    .replaceAll(" ", "-")
    .replaceAll(/[^\w-]/gi, "");
};

const AccordionItem: React.FC<AccordionItemProps> = ({
  id,
  defaultOpen = false,
  className,
  headerClassName,
  header,
  contentClassName,
  content,
}) => {
  id =
    id ??
    (typeof header === "string"
      ? convertHeaderToLowercaseKebab(header)
      : "react.child");

  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className={cn(className)}>
      <h3>
        <button
          role="tab"
          type="button"
          id={`accordion-item-${id}`}
          aria-controls={`accordion-contents-${id}`}
          aria-expanded={isOpen ? "true" : "false"}
          onClick={handleToggle}
          className={cn(
            "flex w-full items-center justify-between bg-white px-4 py-2",
            "text-start font-montserrat text-subheading-3-400 text-neutral-500",
            "transition-colors ease-out",
            isOpen && "bg-primary-100 text-subheading-3-500 text-neutral-600",
            headerClassName
          )}
        >
          {header}
          <span>{isOpen ? <FaChevronUp /> : <FaChevronDown />}</span>
        </button>
      </h3>
      <div
        id={`accordion-contents-${id}`}
        role="tabpanel"
        aria-labelledby={`accordion-item-${id}`}
        className={cn(
          "flex flex-col px-4 py-2 font-roboto text-body-1-400 text-neutral-600",
          "transition ease-out",
          isOpen && "flex bg-primary-100",
          !isOpen && "hidden",
          contentClassName
        )}
      >
        {typeof content === "string" ? (
          <div
            className="rich-text [&_a]:hover:text-primary-700 [&_a]:focus:text-primary-700 [&_a]:rounded-md [&_a]:font-roboto [&_a]:text-body-1-400 [&_a]:text-primary-600 [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:no-underline [&_a]:focus:no-underline [&_a]:focus:outline [&_a]:focus:outline-1 [&_a]:focus:outline-offset-2 [&_a]:focus:outline-black"
            dangerouslySetInnerHTML={{ __html: content || "" }}
          />
        ) : (
          content
        )}
      </div>
    </div>
  );
};

export default AccordionItem;
